import {
  detectBrowser,
  eventBus,
  EventBusType,
  getMarginBottomClass,
  gridStyles,
  marginBottomLevels,
  withContext,
} from '@piggybank/core';
import cn from 'classnames';
import { useCombobox } from 'downshift';
import debounce from 'lodash/debounce';
import pick from 'lodash/fp/pick';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Consumer } from '../Field/context';
import styles from './filterable-select.css';
import InlineAutocomplete from './InlineAutocomplete';
import RemoveButton from './RemoveButton';
import Suggestion from './Suggestion';
import ToggleMenuButton from './ToggleMenuButton';
import useAssistiveHintsSetter from './useAssistiveHintsSetter';

const itemToString = (item) => (item ? item.label : '');

const clearOnBlur = (state, changes) => {
  const { selectedItem, inputValue } = changes;

  if (isNil(selectedItem)) {
    return { ...changes, selectedItem: null, inputValue: '' };
  }
  if (selectedItem !== null && itemToString(selectedItem) !== inputValue) {
    return { ...changes, inputValue: itemToString(selectedItem) };
  }
  return changes;
};

const stateReducer = (props) => (state, actionAndChanges) => {
  const { type, changes } = actionAndChanges;
  switch (type) {
    case useCombobox.stateChangeTypes.InputKeyDownEnter:
    case useCombobox.stateChangeTypes.ItemClick:
      return props.dontUpdateInputOnSelection
        ? { ...changes, inputValue: '' }
        : changes;
    case useCombobox.stateChangeTypes.InputBlur:
    case useCombobox.stateChangeTypes.ToggleButtonClick:
    case useCombobox.stateChangeTypes.InputKeyDownEscape:
      return clearOnBlur(state, changes);
    default:
      return changes;
  }
};

/*
 * getA11yStatusMessage compares selectedItem and resultCount with values from previous call.
 * Additionally it's debounced in some circumstances. So as not to miss any value change it stores previous args here.
 */
let prevArgs = {};
const getA11yStatusMessage = ({
  getSelectionMessage,
  noResults,
  getResultCountMessage,
}) => ({ isOpen, resultCount, selectedItem, itemToString, isMobile }) => {
  const {
    selectedItem: prevSelectedItem,
    resultCount: prevResultCount,
  } = prevArgs;
  prevArgs = { selectedItem, resultCount };

  if (selectedItem && !isEqual(selectedItem, prevSelectedItem)) {
    return getSelectionMessage({
      itemToString,
      selectedItem,
    });
  }

  if (!isOpen) {
    return '';
  }

  if (!resultCount) {
    return noResults;
  }

  if (resultCount !== prevResultCount) {
    return getResultCountMessage({ resultCount, isMobile });
  }

  return '';
};

const defaultFilterItems = (items, inputValue) =>
  items.filter((item) =>
    itemToString(item).toLowerCase().startsWith(inputValue.toLowerCase())
  );

const getInlineAutocompleteProps = (
  hideInlineAutocomplete,
  isOpen,
  inputValue,
  selectedItem,
  highlightedIndex,
  suggestions
) => {
  const highlightedLabel = itemToString(suggestions[highlightedIndex]);
  const isActive =
    !hideInlineAutocomplete &&
    isOpen &&
    inputValue !== itemToString(selectedItem) &&
    !!inputValue &&
    !!highlightedLabel;

  return {
    isActive,
    inputValue,
    highlightedLabel,
  };
};

/**
 * iOS VoiceOver users double tap to select an item. Unfortunately it recognises the second tap
 * as a click outside the elements list and moves focus to the next element in the form.
 * To prevent that behavior downshift's onClick function is debounced only for iOS users.
 */
const getCustomizedItemProps = ({
  getItemProps,
  suggestion,
  index,
  setPressedItem,
  debouncedItemOnClickRef,
}) => {
  const itemProps = getItemProps({
    item: suggestion.value,
    index,
    onMouseDown: () => setPressedItem(index),
    onMouseLeave: () => setPressedItem(-1),
    onMouseUp: () => setPressedItem(-1),
  });

  if (!detectBrowser.iOS) {
    return itemProps;
  }

  const { onClick, ...rest } = itemProps;

  if (!debouncedItemOnClickRef.current) {
    debouncedItemOnClickRef.current = {};
  }

  if (!debouncedItemOnClickRef.current[index]) {
    debouncedItemOnClickRef.current[index] = debounce(onClick, 200);
  }

  return {
    ...rest,
    onClick: () => {
      debouncedItemOnClickRef.current[index]('');
    },
  };
};

const FilterableSelect = React.forwardRef(
  (
    {
      marginBottom,
      items,
      onChange,
      required,
      describers,
      fullWidth,
      invalid,
      name,
      disabled,
      value,
      onBlur,
      filterItems,
      initialIsOpen,
      hideInlineAutocomplete,
      textMap,
      hideToggleButton,
      hideRemoveButton,
      renderBefore,
      renderSuggestion,
      renderSuggestionText,
      dontUpdateInputOnSelection,
    },
    ref
  ) => {
    const debouncedItemOnClickRef = useRef(null);
    const { clearSelectionAriaLabel, noResults, toggleMenuAriaLabel } = textMap;
    const [suggestions, setSuggestions] = useState(items);
    const [pressedItemIndex, setPressedItem] = useState(-1);
    const [textInputFocused, setIsTextInputFocused] = useState(false);
    const isInitialMountRef = useRef(true);

    const mainRef = useRef();
    const inputRef = ref ? ref : mainRef;

    const doFilterItems = filterItems ? filterItems : defaultFilterItems;

    // value other than undefined means that user treats FilterableSelect as controlled component.
    const selectedItemArg =
      value === undefined
        ? value
        : items.find((x) => x.value === value) || null;

    const {
      isOpen,
      selectedItem,
      getToggleButtonProps,
      getMenuProps,
      getInputProps,
      getComboboxProps,
      highlightedIndex,
      getItemProps,
      reset,
      inputValue,
    } = useCombobox({
      // Because of Downshift's issues assistive hints feature is provided by useAssistiveHintsSetter
      getA11ySelectionMessage: () => '',
      getA11yStatusMessage: () => '',

      initialIsOpen: !!initialIsOpen,
      id: name,
      labelId: `${name}-field-label`,
      inputId: `${name}-field`,
      items: suggestions,
      initialSelectedItem: selectedItemArg,
      selectedItem: selectedItemArg,
      itemToString: itemToString,
      onInputValueChange: ({ inputValue }) => {
        Promise.resolve(doFilterItems(items, inputValue)).then((results) =>
          setSuggestions(results)
        );
      },
      stateReducer: stateReducer({ dontUpdateInputOnSelection }),
      defaultHighlightedIndex: 0,
      onSelectedItemChange: (val) => {
        if (onChange) {
          const arg = val.selectedItem ? val.selectedItem : { value: '' };
          eventBus.dispatch({
            type: EventBusType.ON_CHANGE,
            component: 'FilterableSelect',
            value: arg.value,
            name,
          });
          onChange(arg);
        }
      },
    });

    const resultCount = suggestions.length;
    useAssistiveHintsSetter(
      getA11yStatusMessage(textMap),
      isInitialMountRef.current,
      [isOpen, resultCount, selectedItem],
      {
        isOpen,
        resultCount: suggestions.length,
        selectedItem,
        itemToString,
        isMobile: detectBrowser.android || detectBrowser.iOS,
      }
    );

    useEffect(() => {
      isInitialMountRef.current = false;
    }, []);

    return (
      <div
        className={cn(
          styles.inputWrapper,
          {
            [styles.focused]: textInputFocused,
            [styles.disabled]: disabled,
            [styles.invalid]: invalid,
            [styles.fullWidth]: fullWidth,
            [gridStyles['size-12']]: !fullWidth,
            [gridStyles['sizeMedium-7']]: !fullWidth,
            [gridStyles['sizeLarge-4']]: !fullWidth,
          },
          getMarginBottomClass(marginBottom)
        )}
      >
        {renderBefore && (
          <div
            className={styles.renderBeforeClickableWrapper}
            onClick={() => inputRef.current?.focus()}
            aria-hidden="true"
          >
            {renderBefore({
              textInputFocused,
              selectedItem,
              isOpen,
            })}
          </div>
        )}

        <div className={styles.innerWrapper} {...getComboboxProps()}>
          <InlineAutocomplete
            {...getInlineAutocompleteProps(
              hideInlineAutocomplete,
              isOpen,
              inputValue,
              selectedItem,
              highlightedIndex,
              suggestions
            )}
          />
          <input
            className={cn(styles.textInput, styles.textInputPadding)}
            aria-describedby={describers}
            aria-invalid={invalid}
            {...getInputProps({
              disabled,
              ref: inputRef,
              required,
              onFocus: () => setIsTextInputFocused(true),
              onBlur: (e) => {
                setIsTextInputFocused(false);
                if (onBlur) {
                  onBlur(e);
                }
              },
            })}
          />
          {!hideRemoveButton && !!selectedItem && !disabled && (
            <RemoveButton
              clearSelectionAriaLabel={clearSelectionAriaLabel}
              inputRef={inputRef}
              reset={reset}
            />
          )}
          {!hideToggleButton && (
            <ToggleMenuButton
              disabled={disabled}
              getToggleButtonProps={getToggleButtonProps}
              isOpen={isOpen}
              toggleMenuAriaLabel={toggleMenuAriaLabel}
            />
          )}
        </div>
        <ul
          className={cn(styles.dropDownList, {
            [styles.dropDownListClosed]: !isOpen,
          })}
          {...getMenuProps()}
        >
          {isOpen &&
            suggestions.map((suggestion, index) => {
              const isHighlighted = index === highlightedIndex;
              const isPressed = index === pressedItemIndex;
              const isSelected =
                suggestion.value === (selectedItem ? selectedItem.value : '');

              const itemProps = getCustomizedItemProps({
                getItemProps,
                suggestion,
                index,
                setPressedItem,
                debouncedItemOnClickRef,
              });

              return (
                <li key={`${suggestion.value}${index}`} {...itemProps}>
                  {renderSuggestion &&
                    renderSuggestion({
                      inputValue,
                      suggestion,
                      isHighlighted,
                      isPressed,
                      isSelected,
                    })}
                  {!renderSuggestion && (
                    <Suggestion
                      isHighlighted={isHighlighted}
                      isPressed={isPressed}
                      isSelected={isSelected}
                      inputValue={inputValue}
                      label={itemToString(suggestion)}
                      renderSuggestionText={renderSuggestionText}
                    />
                  )}
                </li>
              );
            })}
          {!suggestions.length && isOpen && (
            <li className={cn(styles.dropDownListItem, styles.noResultsFound)}>
              {noResults}
            </li>
          )}
        </ul>
      </div>
    );
  }
);

FilterableSelect.displayName = 'FilterableSelect';

const getSelectionMessageDefault = ({ selectedItem, itemToString }) =>
  `${itemToString(selectedItem)} has been selected.`;

const getResultCountMessageDefault = ({
  resultCount,
  isMobile,
}) => `${resultCount} result${resultCount === 1 ? ' is' : 's are'} available, ${
  isMobile
    ? 'explore by touch or with swipe gestures.'
    : 'use up and down arrow keys to navigate. Press Enter key to select.'
} 
`;

FilterableSelect.defaultProps = {
  renderBefore: null,
  renderSuggestion: null,
  renderSuggestionText: null,
  required: true,
  hideToggleButton: false,
  hideRemoveButton: false,
  items: [],
  hideInlineAutocomplete: false,
  textMap: {
    clearSelectionAriaLabel: 'Clear selection',
    noResults: 'No results found.',
    toggleMenuAriaLabel: 'Toggle menu',
    getSelectionMessage: getSelectionMessageDefault,
    getResultCountMessage: getResultCountMessageDefault,
  },
};

FilterableSelect.propTypes = {
  /**
   * Array of all selectable options. Shape: `[{ label, value }]`
   */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  /**
   * Custom filter function to determine applicable suggestions. `(items, inputValue) => [item]`
   */
  filterItems: PropTypes.func,
  /**
   * Returned component replaces Suggestion and makes renderSuggestionText not work.
   * `({inputValue, suggestion, isHighlighted, isPressed, isSelected}) => Node`
   */
  renderSuggestion: PropTypes.func,
  /**
   * Returned component replaces Suggestion text's node. `(label, inputValue) => Node`
   * It has no effect if renderSuggestion used.
   */
  renderSuggestionText: PropTypes.func,
  /**
   * Whether the suggestions list should be expanded on mount
   */
  initialIsOpen: PropTypes.bool,
  /**
   * Whether the grey autocompletion overlay text should be rendered over the textbox
   */
  hideInlineAutocomplete: PropTypes.bool,
  /** docgen-from-context:<Field/> */
  onChange: PropTypes.func.isRequired,
  /** docgen-from-context:<Field/> */
  name: PropTypes.string.isRequired,
  /**
   * If FilterableSelect component is not used in Form/Field ecosystem then value doesn't have to be used as state is controlled internally. Use it to change selectedItem externally.
   */
  value: PropTypes.string,
  /** docgen-from-context:<Field/> */
  required: PropTypes.bool,
  /** docgen-from-context:<Field/> */
  invalid: PropTypes.bool,
  /** docgen-from-context:<Field/> */
  onBlur: PropTypes.func,
  fullWidth: PropTypes.bool,
  /** docgen-from-context:<Field/> */
  describers: PropTypes.string,
  /**
   * Whether the input is greyed-out and non-interactive
   */
  disabled: PropTypes.bool,
  /**
   * 0, 1, 2, 3, 4, 5, 6, 7
   */
  marginBottom: PropTypes.oneOf(marginBottomLevels),
  textMap: PropTypes.shape({
    noResults: PropTypes.string,
    clearSelectionAriaLabel: PropTypes.string,
    toggleMenuAriaLabel: PropTypes.string,
    getSelectionMessage: PropTypes.func,
    getResultCountMessage: PropTypes.func,
  }),
  /**
   * Whether the toggle menu button is hidden.
   */
  hideToggleButton: PropTypes.bool,
  /**
   * Whether the remove button is hidden.
   */
  hideRemoveButton: PropTypes.bool,
  /**
   * Whether the before slot is rendered `({textInputFocused, selectedItem, isOpen}) => Node`
   */
  renderBefore: PropTypes.func,

  /**
   * Use it if you don't want to see selected item after selection in the input search box. The input field will still be pre-populated.
   */
  dontUpdateInputOnSelection: PropTypes.bool,
};

export { FilterableSelect };

export default withContext(
  Consumer,
  pick([
    'name',
    'value',
    'onChange',
    'onBlur',
    'invalid',
    'required',
    'describers',
  ])
)(FilterableSelect);
